import { useState, useEffect, useMemo } from "react";
import { Button, Modal, Input, Spin, message } from "antd";
import { Plus, X } from "react-feather";
import styles from "./stylesTopMarkets.module.css";
// Import AddNewIpPopup styles for consistent modal styling
import "../../../WhiteList/AddNewIpPopup/stylesAddNewIpPopup.css";
// Import API functions
import { API_ROUTES } from "../../../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
} from "../../../../utils/ErrorUtils";
import SortingTable from "../SortingTable/SortingTable";
import { useSportsbookFm } from "../SportsbookFmContext";
import { RenameEntityModal } from "../Translations/RenameEntityModal";
import {
  applyListEntityRename,
  applyListEntityTranslations,
  getListEntityBaseName,
  toListTranslationEditorData,
} from "../Translations/listTranslationData";
import { useProductFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { TRIVELTA_PRODUCTS } from "../../../../utils/products";

const TopMarkets = () => {
  const { canEdit } = useSportsbookFm();
  const { isEnabled: translationsFlagEnabled } = useProductFeatureFlag(
    "fm_translations",
    TRIVELTA_PRODUCTS.SPORTSBOOK
  );
  const translationsEnabled = translationsFlagEnabled === true;
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeSport, setActiveSport] = useState(null);
  const [activePreMatchLive, setActivePreMatchLive] = useState("PREMATCH");

  // Rename state
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameLoading, setRenameLoading] = useState(false);
  const renameTranslationData = useMemo(
    () =>
      toListTranslationEditorData(
        renameTarget,
        renameTarget?.id,
        renameTarget?.ename ?? ""
      ),
    [renameTarget]
  );
  const renameInitialName = translationsEnabled
    ? getListEntityBaseName(renameTarget, renameTarget?.ename ?? "")
    : (renameTarget?.ename ?? "");

  // Get token and create API instance
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken
  );

  const [popularTabs, setPopularTabs] = useState([]);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [sports, setSports] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(false);


  // API Functions
  const fetchSports = async () => {
    setSportsLoading(true);
    const params = {
      entity_type: activePreMatchLive,
    };
    try {
      const response = await PrivateAdminPanelInstance.post(API_ROUTES.GET_EVENT_MARKETS_SPORTS, { params });
      const sportsData = response.data.sports;
      
      setSports(sportsData);
      
      // Set the first sport as active
      if (sportsData?.length > 0) {
        setActiveSport(sportsData[0].sport_id);
      }
    } catch (error) {
      console.error("Error fetching sports:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setSportsLoading(false);
    }
  };

  const fetchTopMarkets = async () => {
    if (!activeSport) return;
    
    try {
      setIsTableLoading(true);
      const url = API_ROUTES.GET_TOP_MARKETS
        .replace("{sport_id}", activeSport)
        .replace("{event_type}", activePreMatchLive);
      
      const response = await PrivateAdminPanelInstance.get(url);

      if (response.data.success) {
        const topMarketsData = response.data.data.items.map((item) => ({
          id: item?.entity_id || "",
          item_id: item?.item_id || "",
          ename: item?.display_name || "",
          order: item?.display_order?.toString() || "",
          is_deleted: item?.is_deleted ?? false,
          sport_id: item?.sport_id || "",
          event_type: item?.event_type || "",
          market_ids: item?.market_ids || [],
          translations: item?.translations || null,
        }));

        setPopularTabs(topMarketsData);
      }
    } catch (error) {
      console.error("Error fetching top markets:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setIsTableLoading(false);
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setIsAddModalVisible(true);
  };

  const handleModalClose = () => {
    setIsAddModalVisible(false);
    setSearchValue("");
    setSelectedItem(null);
    setSearchOptions([]);
  };

  // Real search function using API
  const fetchSearchOptions = useMemo(() => {
    let timer;
    return (query) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        if (!query || query.length < 2 || !activeSport) {
          setSearchOptions([]);
          return;
        }
        setSearchLoading(true);
        try {
          const url = API_ROUTES.AUTOCOMPLETE_TOP_MARKETS
            .replace("{sport_id}", activeSport)
            .replace("{event_type}", activePreMatchLive)
            .replace("{search_term}", encodeURIComponent(query));

          const response = await PrivateAdminPanelInstance.get(url);

          if (response.data.success) {
            const searchData = response.data.data.map((market) => ({
              entity_id: market.entity_id,
              market_id: market.market_id,
              market_ids: market.market_ids || [parseInt(market.market_id, 10)],
              name: market.name,
              display_name: market.display_name,
            }));
            setSearchOptions(searchData);
          }
        } catch (error) {
          console.error("Error searching markets:", error);
          message.error(
            getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED)
          );
        } finally {
          setSearchLoading(false);
        }
      }, 300);
    };
  }, [activeSport, activePreMatchLive]);

  useEffect(() => {
    fetchSports();
  }, []);

  useEffect(() => {
    fetchTopMarkets();
  }, [activeSport, activePreMatchLive]);

  useEffect(() => {
    fetchSearchOptions(searchValue);
  }, [searchValue, fetchSearchOptions]);

  const handleAddItemConfirm = async () => {
    if (!selectedItem || !activeSport) return;
    setAddLoading(true);
    try {
      const response = await PrivateAdminPanelInstance.post(
        API_ROUTES.CREATE_NEW_TOP_MARKET,
        {
          sport_id: activeSport,
          event_type: activePreMatchLive,
          item_id: selectedItem.market_id,
          display_name: selectedItem.display_name,
          display_order: null,
          market_ids: selectedItem.market_ids,
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );
      setIsAddModalVisible(false);
      setSearchValue("");
      setSelectedItem(null);
      setSearchOptions([]);

      // Refresh the top markets list
      await fetchTopMarkets();
    } catch (error) {
      console.error("Error adding market:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setAddLoading(false);
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const toggleStatus = async (record) => {
    setIsTableLoading(true);

    const encodedId = encodeURIComponent(record.id);

    try {
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.SET_POPULAR_TAB_STATUS + "/" + encodedId + "/set-active",
        {
          is_active: !record.is_active,
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );

      // Update local state
      setPopularTabs((prevData) =>
        prevData.map((item) =>
          item.id === record.id
            ? { ...item, is_active: !item.is_active }
            : item
        )
      );
    } catch (error) {
      console.error("Error toggling item status:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setIsTableLoading(false);
    }
  };

  const removeItem = async (record) => {
    setIsTableLoading(true);
    
    try {
      const response = await PrivateAdminPanelInstance.delete(
        API_ROUTES.DELETE_TOP_MARKET,
        { data: { entity_id: record.id } }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );

      // Update local state
      setPopularTabs((prevData) =>
        prevData.filter((item) => item.id !== record.id)
      );
    } catch (error) {
      console.error("Error deleting market:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
    } finally {
      setIsTableLoading(false);
    }
  };

  const changeOrder = async (reorderedItems) => {
    setIsTableLoading(true);
    try {
      // Update local state first for immediate UI feedback
      setPopularTabs(reorderedItems);

      // Send API request to reorder
      const orderedIds = reorderedItems.map((item) => item.id);
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.ORDER_TOP_MARKETS,
        {
          ordered_ids: orderedIds,
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED)
      );
    } catch (error) {
      console.error("Error reordering top markets:", error);
      message.error(getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED));
      // Revert to original state on error
      fetchTopMarkets();
    } finally {
      setIsTableLoading(false);
    }
  };

  // Rename handlers
  const handleRenameClick = (item) => {
    setRenameTarget(item);
    setIsRenameModalVisible(true);
  };

  const handleRenameModalClose = () => {
    setIsRenameModalVisible(false);
    setRenameTarget(null);
  };

  const handleRenameConfirm = async (name, translationChanges) => {
    if (!renameTarget) return;
    setRenameLoading(true);
    try {
      const response = await PrivateAdminPanelInstance.patch(
        API_ROUTES.RENAME_TOP_MARKET,
        {
          entity_id: renameTarget.id,
          display_name: name,
          ...(translationChanges
            ? {
                translation_key: translationChanges.translation_key,
                translations: translationChanges.translations,
              }
            : {}),
        }
      );

      message.success(
        getApiSuccessMessage(response, AppStrings.ITEM_RENAMED_SUCCESSFULLY)
      );

      // Update local state
      setPopularTabs((prevData) =>
        prevData.map((item) => {
          if (item.id !== renameTarget.id) return item;
          const renamed = applyListEntityRename(
            item,
            name,
            translationsEnabled,
            "ename"
          );
          if (!translationChanges) return renamed;
          return applyListEntityTranslations(
            renamed,
            translationChanges.translations,
            "ename",
            translationChanges.translation_key
          );
        })
      );

      handleRenameModalClose();
    } catch (error) {
      console.error("Error renaming item:", error);
      message.error(getApiErrorMessage(error, AppStrings.FAILED_TO_RENAME_ITEM));
    } finally {
      setRenameLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.emSportsHeaderContainer}>
        <div className={styles.emSportsList}>
          {sportsLoading ? (
            <Spin size="small" />
          ) : (
            sports.map((s) => (
              <div
                key={s.sport_id}
                className={`${styles.emSportPill} ${activeSport === s.sport_id ? styles.active : ""
                  }`}
                onClick={() => setActiveSport(s.sport_id)}
              >
                <span>{s.name}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className={styles.preMatchLiveToggle}>
        <button className={activePreMatchLive === "PREMATCH" ? styles.active : ""} onClick={() => setActivePreMatchLive("PREMATCH")}>
          {AppStrings.PRE_MATCH || "Pre-Match"}

        </button>
        <button className={activePreMatchLive === "LIVE" ? styles.active : ""} onClick={() => setActivePreMatchLive("LIVE")}>
          {AppStrings.LIVE || "Live"}
        </button>
      </div>

      <div className={styles.header}>
        <h2>{AppStrings.TOP_MARKETS}</h2>
        {canEdit && (
          <Button
            type="primary"
            icon={<Plus size={16} />}
            onClick={handleAddItem}
            className={styles.addButton}
          >
            {AppStrings.ADD_MARKET || "Add Market"}
          </Button>
        )}
      </div>
      <div className={styles.tableContainer}>
        <SortingTable
          columns={[
            "",
            "#Order",
            "Name",
            "Markets", 
            "Action",
          ]}
          data={popularTabs}
          setData={setPopularTabs}
          toggleStatus={toggleStatus}
          removeItem={removeItem}
          onRename={handleRenameClick}
          changeOrder={changeOrder}
          isLoading={isTableLoading}
          canEdit={canEdit}
        />
      </div>

      <Modal
        closable={false}
        open={isAddModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        className="add-league-modal"
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">Add Market</h2>
            </div>
            <X onClick={handleModalClose} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <div className="autocomplete">
              <Input
                placeholder="Search markets..."
                value={searchValue}
                onChange={handleSearchChange}
              />
              {searchValue ? (
                <div>
                  {searchLoading ? (
                    <div className="center">
                      <Spin size="small" />
                    </div>
                  ) : (
                    searchOptions?.map((option) => (
                      <div
                        key={option.entity_id}
                        className={`${styles.leagueCard} ${selectedItem?.entity_id === option.entity_id
                          ? styles.selected
                          : ""
                          }`}
                        onClick={() => handleItemSelect(option)}
                      >
                        <div className={styles.leagueInfo}>
                          <span className={styles.leagueName}>
                            {option.display_name}
                          </span>
                        </div>
                        <div className={styles.marketIdTag}>
                          <span className={styles.marketIdText}>
                            IDs: {option.market_ids.join(', ')}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                  {!searchLoading &&
                    searchOptions.length === 0 &&
                    searchValue && (
                      <div className="empty-mini">
                        No results found
                      </div>
                    )}
                </div>
              ) : null}
            </div>
          </div>
          <div className="modal-footer">
            <button
              className="cancel-button"
              onClick={handleModalClose}
              disabled={addLoading}
            >
              {AppStrings.CLOSE}
            </button>
            <button
              className={`submit-button ${!selectedItem || addLoading ? "disabled" : ""
                }`}
              disabled={!selectedItem || addLoading}
              onClick={handleAddItemConfirm}
            >
              {addLoading ? AppStrings.ADDING : AppStrings.ADD_MARKET}
            </button>
          </div>
        </div>
      </Modal>

      <RenameEntityModal
        open={isRenameModalVisible}
        title={AppStrings.RENAME_TOP_MARKET}
        nameLabel={AppStrings.DISPLAY_NAME}
        initialName={renameInitialName}
        entityKey={renameTarget?.translations?.key ?? renameTarget?.id}
        initialTranslationData={renameTranslationData}
        translationsEnabled={translationsEnabled}
        bundleTranslationChanges={translationsEnabled}
        saving={renameLoading}
        onSave={handleRenameConfirm}
        onClose={handleRenameModalClose}
      />
    </div>
  );
};

export default TopMarkets;
